import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  Gauge as GaugeIcon, Users as UsersIcon, Package as PackageIcon, Truck as TruckIcon,
  ShoppingCart as ShoppingCartIcon, CurrencyDollar as CurrencyDollarIcon, ChartLineUp as ChartLineUpIcon,
  IdentificationBadge as IdentificationBadgeIcon, Storefront as StorefrontIcon, ShieldCheck as ShieldCheckIcon,
  Robot as RobotIcon, Bell as BellIcon, Sun as SunIcon, Moon as MoonIcon, SignOut as SignOutIcon,
  List as ListIcon, X as XIcon, ClipboardText as ClipboardTextIcon,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: GaugeIcon, end: true },
  { to: "/customers", label: "Customers & Credit", icon: UsersIcon },
  { to: "/inventory", label: "Inventory", icon: PackageIcon },
  { to: "/fleet", label: "Fleet", icon: TruckIcon },
  { to: "/pos", label: "Sales / POS", icon: ShoppingCartIcon },
  { to: "/daily-balance", label: "Daily Balance", icon: ClipboardTextIcon },
  { to: "/financial", label: "Financial", icon: CurrencyDollarIcon },
  { to: "/suppliers", label: "Suppliers", icon: StorefrontIcon },
  { to: "/employees", label: "Employees", icon: IdentificationBadgeIcon },
  { to: "/safety", label: "Safety & Compliance", icon: ShieldCheckIcon },
  { to: "/reports", label: "Reports", icon: ChartLineUpIcon },
  { to: "/assistant", label: "AI Assistant", icon: RobotIcon },
];

function NotificationBell() {
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    api.get("/notifications").then((r) => setNotes(r.data)).catch(() => {});
  }, []);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-btn" className="relative p-2 rounded-md hover:bg-secondary transition-colors">
          <BellIcon size={20} weight="duotone" />
          {notes.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-destructive text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
              {notes.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b font-head font-semibold text-sm">Notifications Center</div>
        <div className="max-h-96 overflow-auto">
          {notes.length === 0 && <div className="p-4 text-sm text-muted-foreground">All clear. No alerts.</div>}
          {notes.map((n) => (
            <div key={n.id} className="px-4 py-3 border-b last:border-0 flex gap-3 items-start">
              <span className={`mt-1 h-2 w-2 rounded-full ${n.level === "danger" ? "bg-destructive" : "bg-amber-500"}`} />
              <div>
                <div className="text-sm font-medium">{n.title}</div>
                <div className="text-xs text-muted-foreground">{n.message}</div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const doLogout = async () => { await logout(); nav("/login"); };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center border-b border-border/60">
        <Logo size={38} showText />
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors border-l-2 ${
                  isActive
                    ? "bg-accent text-accent-foreground border-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`
              }
            >
              <Icon size={19} weight="duotone" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border/60">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-head font-semibold text-sm">
            {(user?.name || "U").slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground capitalize">{user?.role}</div>
          </div>
          <button data-testid="logout-btn" onClick={doLogout} className="p-2 rounded-md hover:bg-destructive/10 hover:text-destructive transition-colors">
            <SignOutIcon size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 border-r border-border bg-card">{SidebarInner}</aside>

      {/* mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 bg-card border-r border-border">{SidebarInner}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 border-b border-border glass sticky top-0 z-30 flex items-center gap-3 px-4 lg:px-6">
          <button className="lg:hidden p-2 rounded-md hover:bg-secondary" onClick={() => setOpen(true)} data-testid="menu-toggle">
            {open ? <XIcon size={20} /> : <ListIcon size={20} />}
          </button>
          <div className="lg:hidden"><Logo size={32} /></div>
          <div className="hidden lg:flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-head font-semibold text-foreground text-base">
              {NAV.find((n) => n.to === loc.pathname)?.label || "Overview"}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <Badge variant="outline" className="hidden sm:flex text-[11px] font-normal border-primary/30 text-primary">
              San Juan, Belize
            </Badge>
            <NotificationBell />
            <button
              data-testid="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 rounded-md hover:bg-secondary transition-colors"
            >
              {theme === "dark" ? <SunIcon size={20} weight="duotone" /> : <MoonIcon size={20} weight="duotone" />}
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
